import { useTranslations } from "../../contexts/Translation";
import { Response as ResponseType, Survey } from "../../types";
import Quote from "../Quote/Quote";
import Invites from "./Invites/Invites";
import { getQuoteName } from "../../lib/getQuoteName";
import { useSurvey } from "../../contexts/Survey";
import getQuestionsForSurveyType from "../../lib/getQuestionsForSurveyType";
import EmScore from "./EmScore";
import { CategoryScores } from "../Dashboard/insights";
import { categoryResults } from "./surveyHistory";
import styles from "./Summary.module.css";

type SummaryProps = {
  responses: ResponseType[];
  survey: Survey;
  previous: Survey | null;
  previousScores?: CategoryScores;
  streak: { category: string; count: number } | null;
};

const formatScore = (score: number) => (Math.round(score * 10) / 10).toString();

const Summary = ({ responses, survey, previous, previousScores, streak }: SummaryProps) => {
  const t = useTranslations("questions");
  const ts = useTranslations("surveys");
  const { isShared } = useSurvey();
  const score = categoryResults(responses);

  const recommendations = responses.filter((r) => !!r.recommendation);
  const messages = responses.filter((r) => !!r.message);
  const questions = getQuestionsForSurveyType(survey.surveyType || "long-term");

  return (
    <div className={styles.summary}>
      <section className={styles.scoreCard} aria-label={ts("totalScore")}>
        <div className={styles.total}>
          <EmScore size={6} status={survey.status} score={score.totalScore} />
          <p className={styles.totalLabel}>{ts("totalScore")}</p>
          <p className={styles.small}>{ts("answersCount", score.answers)}</p>
          <p className={styles.small}>{ts("dontKnowsCount", score.dontKnows)}</p>
          {previous?.score ? <p className={styles.small}>{ts("previousSurvey", formatScore(Number(previous.score)))}</p> : null}
        </div>
        <ul className={styles.categories}>
          {questions.map((q) => {
            const result = score.questions[q] || { answers: 0, dontKnows: 0, score: 0 };
            const previousScore = previousScores?.[q];
            return (
              <li key={q} className={styles.category}>
                <EmScore labelVariant='compact' size={3} score={result.score} />
                <div className={styles.categoryText}>
                  <p className={styles.categoryName}>
                    {t(`${q}Label`)}
                    {streak?.category === q && <span className={styles.streak}>{ts("lowestStreak", streak.count)}</span>}
                  </p>
                  <p className={styles.small}>{t(q, survey.candidate)}</p>
                  <p className={styles.small}>
                    {t("resultAnswers", result.answers)} · {t("resultDontKnows", result.dontKnows)}
                    {previousScore !== undefined && <> · {ts("previousScore", formatScore(previousScore))}</>}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
      <p className={styles.note}>{ts("strongScore")}</p>

      {messages.length > 0 && (
        <section className={styles.block}>
          <h2 className={styles.blockHeading}>{t("messagesHeading")}</h2>
          <div className={styles.messages}>
            {messages.map((r, index) => (
              <blockquote key={index} className={styles.message}>
                <p>{r.message}</p>
                <footer>{t("feedbackFromHeading", r.responder)}</footer>
              </blockquote>
            ))}
          </div>
        </section>
      )}

      {recommendations.length > 0 && (
        <section className={styles.block}>
          <h2 className={styles.blockHeading}>{t("recommendationsHeading")}</h2>
          {recommendations.map((r) => (
            <Quote key={r.responder} from={getQuoteName(r.recommendationName, r.recommendationTitle, r.responder)}>
              {r.recommendation}
            </Quote>
          ))}
        </section>
      )}

      {!isShared && survey.status === "ongoing" && (
        <section className={styles.block}>
          <h2 className={styles.blockHeading}>{t("invitedHeading")}</h2>
          <Invites surveyId={survey.id} />
        </section>
      )}
    </div>
  );
};

export default Summary;
